import type { SeatLabelMap } from '../seat-labels.js';
import { viewerZoneName, type FieldLane, type FieldLine } from './field-coords.js';

/**
 * Turn an engine AFL event into a single reader-friendly sentence.
 *
 * The engine emits structured events (type + key/value payload). The
 * event log previously dumped those payloads as `key=value` debug text;
 * this maps the common AFL event types to plain narration instead, with
 * seat-ids resolved to "You" / the opponent's label. Unknown types fall
 * back to `null` so the caller can use its generic rendering.
 */

const LINE_NAME: Record<string, string> = {
  d50: 'defensive 50',
  hb: 'half-back',
  centre: 'the centre',
  hf: 'half-forward',
  f50: 'forward 50',
};

function lineName(line: unknown): string {
  return (typeof line === 'string' && LINE_NAME[line]) || String(line ?? 'the field');
}

/** The seat the reader owns — the one labelled "You" (null for spectators). */
function viewerSeatOf(labels?: SeatLabelMap): string | null {
  if (!labels) return null;
  for (const [id, l] of Object.entries(labels)) if (l === 'You') return id;
  return null;
}

const VALID_LINES = new Set(['d50', 'hb', 'centre', 'hf', 'f50']);
const VALID_LANES = new Set(['flank_a', 'corridor', 'flank_b']);

/**
 * A cell named in the READER's frame. Engine payloads carry line/lane in
 * the ACTING side's frame; the same physical cell is the mirror line for
 * the other seat — so "Forward Pocket" in the bot's frame is YOUR back
 * pocket, and naming it unmirrored reads backwards. Returns null when the
 * raw coordinates aren't available (older events) so callers can fall back
 * to the baked possessor-frame name.
 */
function cellForViewer(
  line: unknown,
  lane: unknown,
  frameSeatId: unknown,
  labels?: SeatLabelMap,
): string | null {
  if (typeof line !== 'string' || !VALID_LINES.has(line)) return null;
  if (typeof lane !== 'string' || !VALID_LANES.has(lane)) return null;
  const viewer = viewerSeatOf(labels);
  if (!viewer || typeof frameSeatId !== 'string') return null;
  return viewerZoneName(line as FieldLine, lane as FieldLane, frameSeatId === viewer);
}

/** "Spearhead (in position, tier 1, power 5)" — one contest side's standing,
 *  or null. Contests resolve position → tier → power; the narration reads as
 *  English (no "T1" shorthand). */
function contestSideMath(side: unknown): string | null {
  if (!side || typeof side !== 'object') return null;
  const d = side as { name?: string; inPosition?: boolean; tier?: string | null; stat?: number };
  if (typeof d.inPosition !== 'boolean') return null;
  const name = (d.name ?? 'player').replace(/^The\s+/i, '');
  const tierNum = d.tier ? String(d.tier).replace(/^T/i, '') : '';
  const where = d.inPosition
    ? `in position${tierNum ? `, tier ${tierNum}` : ''}`
    : 'out of position';
  return `${name} (${where}, power ${d.stat ?? 0})`;
}

export function formatAflEvent(
  type: string,
  payload: Record<string, unknown>,
  seatLabels?: SeatLabelMap,
): string | null {
  const label = (seatId: unknown): string => {
    const id = typeof seatId === 'string' ? seatId : '';
    return (seatLabels && seatLabels[id]) || (id ? `${id.slice(0, 6)}…` : 'someone');
  };
  // Possessive form: "Your" for the viewer, "<label>'s" for the opponent.
  const poss = (seatId: unknown): string => {
    const l = label(seatId);
    return l === 'You' ? 'Your' : `${l}'s`;
  };
  const p = payload ?? {};
  // Card names are stylised with a leading "The" ("The Mosquito"); drop it
  // after a possessive so we get "Your Mosquito", not "Your The Mosquito".
  const player =
    typeof p['player'] === 'string' ? (p['player'] as string).replace(/^The\s+/i, '') : null;

  switch (type) {
    case 'afl.match.initialized':
      return 'Match starts — centre ruck for Quarter 1.';
    case 'afl.environment.set': {
      const weather =
        p['weather'] && typeof p['weather'] === 'object'
          ? (p['weather'] as { name?: string }).name
          : null;
      const stadium =
        p['stadium'] && typeof p['stadium'] === 'object'
          ? (p['stadium'] as { name?: string }).name
          : null;
      const parts = [weather, stadium].filter(Boolean);
      return parts.length ? `Conditions: ${parts.join(' · ')}.` : 'Match conditions set.';
    }
    case 'afl.deck.swapped':
      return null; // housekeeping — not worth a line

    // ── Ruck / clearance ────────────────────────────────────────────
    case 'afl.ruck.started':
      return `Ruck contest (Q${p['quarter'] ?? '?'}).`;
    case 'afl.ruck.nominated':
      return `${label(p['seatId'])} nominate${label(p['seatId']) === 'You' ? '' : 's'} a card for the ruck.`;
    case 'afl.ruck.revealed': {
      const details = p['cardDetails'] as
        | Record<string, { name?: string; kind?: string; power?: number; inPosition?: boolean; tier?: string | null }>
        | undefined;
      if (!details) return 'Ruck cards revealed.';
      const sides = Object.entries(details).map(([seatId, d]) => {
        // A ruckman in position shows its tier; a non-ruckman shows it's not a
        // ruckman. A non-player nomination (a bluff) shows its kind. Plain words.
        const tierNum = d.tier ? String(d.tier).replace(/^T/i, '') : '';
        const desc =
          d.kind === 'player'
            ? d.inPosition
              ? `${d.name} (ruckman${tierNum ? `, tier ${tierNum}` : ''}, power ${d.power})`
              : `${d.name} (not a ruckman, power ${d.power})`
            : `${d.name} (${d.kind})`;
        return `${label(seatId)}: ${desc}`;
      });
      return `Ruck reveal — ${sides.join(' v ')}.`;
    }
    case 'afl.ruck.won':
      return `${label(p['winnerSeatId'])} win${label(p['winnerSeatId']) === 'You' ? '' : 's'} the ruck — ${label(p['loserSeatId'])} roves the tap first.`;
    case 'afl.ruck.forfeit':
      return `${label(p['winnerSeatId'])} win${label(p['winnerSeatId']) === 'You' ? '' : 's'} the ruck — ${label(p['loserSeatId'])} didn't contest.`;
    case 'afl.ruck.restarted':
      return 'Ruck tied — nominate again.';
    case 'afl.clearance.started':
      return `Clearance — ${label(p['ruckLoserSeatId'])} rove${label(p['ruckLoserSeatId']) === 'You' ? '' : 's'} the tap first; ${label(p['ruckWinnerSeatId'])} respond${label(p['ruckWinnerSeatId']) === 'You' ? '' : 's'} last.`;
    case 'afl.clearance.commit': {
      const who = `${poss(p['seatId'])} ${player ?? 'player'}`;
      const pw = typeof p['power'] === 'number' ? ` (power ${p['power']})` : '';
      return `${who} roves the tap${pw}.`;
    }
    case 'afl.clearance.ruckman':
      return `Clearance not contested — ${poss(p['winnerSeatId'])} ${player ?? 'ruckman'} gathers the tap.`;
    case 'afl.clearance.resolved': {
      // Position → tier → power. Show the winner's standing (and the loser's),
      // so it's clear WHY: an in-position rover beats an out-of-position one.
      const stand = (inPos: unknown, tier: unknown, pw: unknown) => {
        if (typeof inPos !== 'boolean') return typeof pw === 'number' ? `power ${pw}` : '';
        const tierNum = tier != null && tier !== '' ? String(tier).replace(/^T/i, '') : '';
        return inPos
          ? `in position${tierNum ? `, tier ${tierNum}` : ''}, power ${pw ?? 0}`
          : `out of position, power ${pw ?? 0}`;
      };
      const won = stand(p['inPosition'], p['tier'], p['power']);
      const vs =
        typeof p['otherInPosition'] === 'boolean'
          ? ` v ${stand(p['otherInPosition'], p['otherTier'], p['otherPower'])}`
          : '';
      const up = p['upset'] ? ' — upset, beat the ruck winner!' : '';
      return `Clearance to ${label(p['possessorSeatId'])} — ${player ?? 'their rover'} (${won}${vs}) carries it out${up}.`;
    }
    case 'afl.clearance.tied':
      return 'Clearance tied (same position, tier and power) — back to a ruck.';

    // ── Disposal ────────────────────────────────────────────────────
    case 'afl.pass':
      return p['phase'] === 'ruck_nomination'
        ? `${label(p['seatId'])} don't contest the ruck.`
        : `${label(p['seatId'])} pass.`;
    case 'afl.run': {
      // The payload carries the roll — narrate the OUTCOME. (This used to
      // say "runs the ball forward" even when the run failed, which made a
      // held-up carrier look like a broken feature.)
      const roll =
        typeof p['total'] === 'number' && typeof p['target'] === 'number'
          ? ` (${p['total']} vs ${p['target']})`
          : '';
      const who = `${poss(p['seatId'])} ${player ?? 'player'}`;
      if (p['outcome'] === 'advance')
        return `${who} runs the ball into ${
          cellForViewer(p['to'], p['lane'], p['seatId'], seatLabels) ?? lineName(p['to'])
        }${roll}.`;
      if (p['outcome'] === 'held_up')
        return `${who} tries to run but is held up${roll} — no ground gained.`;
      if (p['outcome'] === 'tackled')
        return `${who} is tackled mid-run${roll} — the ball spills loose.`;
      return `${who} runs the ball forward.`;
    }
    case 'afl.tackle':
      return null; // narrated by the afl.run line above — skip the duplicate
    case 'afl.run.started': {
      const drawer = label(p['defenderSeatId']);
      const dest =
        cellForViewer(p['to'], p['lane'], p['seatId'], seatLabels) ??
        (p['destName'] ?? lineName(p['to']));
      return `${poss(p['seatId'])} ${player ?? 'carrier'} takes off for ${dest} — ${drawer} draw${drawer === 'You' ? '' : 's'} a card and may tackle.`;
    }
    case 'afl.tackle.resolved': {
      const tackler = typeof p['tackler'] === 'string' ? (p['tackler'] as string).replace(/^The\s+/i, '') : 'the tackler';
      const runner = typeof p['runner'] === 'string' ? (p['runner'] as string).replace(/^The\s+/i, '') : 'the runner';
      const speeds = `speed ${p['tacklerSpeed']} vs ${p['runnerSpeed']}`;
      const tIn = p['tacklerInPosition'] === true;
      const rIn = p['runnerInPosition'] === true;
      // A run-down is a contest at the destination cell: in position beats out,
      // then tier, then speed. Out of position there's no tier — it's a foot race.
      const why =
        tIn !== rIn
          ? `${tIn ? tackler : runner} in position`
          : !tIn && !rIn
            ? `neither in position — ${speeds}`
            : p['tacklerTier'] !== p['runnerTier']
              ? `in position, tier ${p['tacklerTier']} vs ${p['runnerTier']}`
              : `tier ${p['tacklerTier']} level — ${speeds}`;
      // Name the skill cards that swung it (the payload carries them) so the
      // log says WHY a tier/speed was lifted, not just the final numbers.
      const m = (p['mods'] ?? {}) as { tackle?: string | null; dash?: string | null };
      const played: string[] = [];
      if (m.tackle) played.push(`${m.tackle} lifted ${tackler}`);
      if (m.dash) played.push(`${m.dash} lifted ${runner}`);
      const skillNote = played.length ? ` [${played.join('; ')}]` : '';
      const atCell =
        cellForViewer(p['fromLine'], p['lane'], p['runnerSeatId'], seatLabels) ??
        (p['at'] ?? 'the line');
      const destCell =
        cellForViewer(p['destLine'], p['lane'], p['runnerSeatId'], seatLabels) ??
        (p['destName'] ?? 'the next line');
      if (p['verdict'] === 'ball_won')
        return `${tackler} runs down ${runner} at ${atCell} (${why})${skillNote} — ball won, turnover!`;
      if (p['verdict'] === 'stoppage')
        return `${tackler} wraps up ${runner} at ${atCell} (${why} — all square)${skillNote} — ball-up.`;
      return `${runner} breaks the tackle from ${tackler} (${why})${skillNote} and carries into ${destCell}.`;
    }
    case 'afl.handpass':
      return `${poss(p['seatId'])} ${player ?? 'player'} handpasses at ${lineName(p['line'])}.`;
    case 'afl.handpass.intercepted':
      return `${poss(p['seatId'])} ${player ?? 'player'} handpass is cut off — turnover to ${label(p['toSeatId'])}.`;
    case 'afl.kick': {
      // Prefer the cell named in the READER's frame; the baked `dir` label
      // is in the kicker's frame and reads backwards for the defender.
      const viewerCell = cellForViewer(p['destLine'], p['destLane'], p['seatId'], seatLabels);
      const direction =
        typeof p['dir'] === 'string' && (p['dir'] as string).includes('·')
          ? ` ·${(p['dir'] as string).split('·').slice(1).join('·')}`
          : '';
      const dest = viewerCell ? `${viewerCell}${direction}` : (p['dir'] ?? lineName(p['destLine']));
      return `${poss(p['seatId'])} ${player ?? 'player'} kicks → ${dest}.`;
    }
    case 'afl.kickto': {
      // Kick-to-space — uncontested, so the bot can chain several of these
      // in one tick. Each MUST narrate, or the ball looks like it teleports.
      const dest = p['dir'] ?? lineName(p['destLine']);
      return p['success'] === false
        ? `${poss(p['seatId'])} ${player ?? 'player'} kicks to space → ${dest} — spills loose.`
        : `${poss(p['seatId'])} ${player ?? 'player'} kicks to space → ${dest} — retained.`;
    }
    case 'afl.kick.declared':
      return `${poss(p['seatId'])} ${player ?? 'player'} lines up a kick — ${label(p['defenderSeatId'])} may smother.`;
    case 'afl.mark.lead': {
      const cell =
        cellForViewer(p['destLine'], p['destLane'], p['seatId'], seatLabels) ?? lineName(p['destLine']);
      const cm = typeof p['contestedMark'] === 'string' && p['contestedMark'] ? ` (${p['contestedMark']})` : '';
      return `${poss(p['seatId'])} ${p['player'] ?? 'a forward'} leads at ${cell}${cm} — the mark is on.`;
    }
    case 'afl.mark.declined': {
      const cell =
        cellForViewer(p['destLine'], p['destLane'], p['seatId'], seatLabels) ?? lineName(p['destLine']);
      return `${poss(p['seatId'])} lead no one at ${cell} — the ball spills, ${label(p['toSeatId'])} to gather.`;
    }
    case 'afl.kick.uncontested': {
      const cell =
        cellForViewer(p['destLine'], p['destLane'], p['kickerSeatId'], seatLabels) ?? lineName(p['destLine']);
      const marker = typeof p['marker'] === 'string' && p['marker'] ? p['marker'] : 'the lead';
      return `Uncontested mark to ${marker} at ${cell} — ${label(p['kickerSeatId'])} retain it.`;
    }
    case 'afl.kick.smothered':
      return `${poss(p['seatId'])} kick is smothered — charged down, loose ball.`;
    case 'afl.kick.contest': {
      const dest =
        cellForViewer(p['destLine'], p['destLane'], p['kickerSeatId'], seatLabels) ??
        ((typeof p['destName'] === 'string' && p['destName']) || lineName(p['destLine']));
      // Newer engines attach the per-side math (stat + position + dice) so
      // the log answers WHY the ball went where it went.
      const marking = (p['marking'] ?? {}) as { kicker?: unknown; responder?: unknown };
      const mk = contestSideMath(marking.kicker);
      const mr = contestSideMath(marking.responder);
      const math = mk && mr ? ` ${mk} v ${mr}.` : '';
      const ground = (p['groundBall'] ?? null) as { kicker?: unknown; responder?: unknown } | null;
      const gk = ground ? contestSideMath(ground.kicker) : null;
      const gr = ground ? contestSideMath(ground.responder) : null;
      const groundMath = gk && gr ? ` Ground ball: ${gk} v ${gr}.` : '';
      // A set/suppress action and/or active position suppression coloured this
      // contest — name it so a tier upset reads as deliberate, not a bug.
      const mods = (p['mods'] ?? {}) as { call?: unknown };
      const callNote = typeof mods.call === 'string' && mods.call ? ` [${mods.call}]` : '';
      const supp = p['suppress'] === 'tier' ? ' [tier disregarded]' : p['suppress'] === 'position' ? ' [position disregarded]' : '';
      const tag = `${callNote}${supp}`;
      switch (p['outcome']) {
        case 'marked_by_kicker':
          return `Marked at ${dest} — ${label(p['kickerSeatId'])} retain${label(p['kickerSeatId']) === 'You' ? '' : 's'} it.${tag}${math}`;
        case 'intercept_mark':
          return `Intercept mark at ${dest} — turnover!${tag}${math}`;
        case 'ground_ball_to_kicker':
          return `Mark tied at ${dest} — ${label(p['kickerSeatId'])} win the ground ball.${tag}${math}${groundMath}`;
        case 'ground_ball_to_responder':
          return `Mark tied at ${dest} — ground ball lost, turnover.${tag}${math}${groundMath}`;
        default:
          return `Ball-up at ${dest} — neutral stoppage.${tag}${math}${groundMath}`;
      }
    }

    // ── Shooting / scoring ──────────────────────────────────────────
    case 'afl.shot': {
      const from = lineName(p['line']);
      // `torpedo` is a kind string ('none' | 'standard' | 'rare'); only the
      // real torpedoes annotate. A played shot modifier names itself instead.
      const isTorp = p['torpedo'] === 'standard' || p['torpedo'] === 'rare';
      const mod =
        typeof p['mod'] === 'string' && p['mod'] ? ` (${p['mod']})` : isTorp ? ' (torpedo)' : '';
      // A tired shooter (stamina ≤ 0) shoots worse — narrate why.
      const tired = typeof p['stamina'] === 'number' && (p['stamina'] as number) < 0 ? ` (tired ${p['stamina']})` : '';
      const extra = `${mod}${tired}`;
      // WHY it was a goal/behind: the kept die vs the needs (owner rule).
      const dice = Array.isArray(p['dice']) ? (p['dice'] as number[]) : null;
      const kept = typeof p['kept'] === 'number' ? (p['kept'] as number) : null;
      const goalNeed = typeof p['goalNeed'] === 'number' ? (p['goalNeed'] as number) : null;
      const behindNeed = typeof p['behindNeed'] === 'number' ? (p['behindNeed'] as number) : null;
      const keep = p['keep'] === 'low' ? 'low' : 'high';
      const why =
        kept != null && goalNeed != null && behindNeed != null
          ? ` — kept the ${keep} die ${kept}${dice ? ` (rolled ${dice.join(' and ')})` : ''}, needed ${goalNeed}+ to goal / ${behindNeed}+ for a behind`
          : '';
      if (p['outcome'] === 'goal') return `Shot from ${from}${extra} — GOAL!${why}`;
      if (p['outcome'] === 'behind') return `Shot from ${from}${extra} — behind${why}.`;
      return `Shot from ${from}${extra} — missed${why}.`;
    }
    case 'afl.shot.miss':
      return `Shot from ${lineName(p['line'])} — missed everything.`;
    case 'afl.score.goal':
      return `GOAL to ${label(p['seatId'])}! (6 points)`;
    case 'afl.score.behind':
      return `Behind to ${label(p['seatId'])}. (1 point)`;
    case 'afl.kickin.pocket':
      return `${label(p['seatId'])} take the kick-in from the ${p['lane'] === 'flank_a' ? 'top' : 'bottom'} pocket.`;

    // ── Stadium auction ─────────────────────────────────────────────
    case 'afl.stadium.opened':
      return `${label(p['seatId'])} play${label(p['seatId']) === 'You' ? '' : 's'} ${p['stadium'] ?? 'a stadium'} and open${label(p['seatId']) === 'You' ? '' : 's'} a venue auction.`;
    case 'afl.stadium.raise':
      return `${label(p['seatId'])} raise${label(p['seatId']) === 'You' ? '' : 's'} the venue bid to ${p['to'] ?? '?'}.`;
    case 'afl.stadium.installed':
      return `${p['stadium'] ?? 'A venue'} installs as the ground — a passive for both sides for the rest of the match.`;
    case 'afl.stadium.tie':
      return `Venue auction tied — no ground set; both stadiums and all bids recycle.`;
    case 'afl.defender.move':
      return `${poss(p['seatId'])} ${player ?? 'defender'} responds${p['action'] ? ` with ${p['action']}` : ''}.`;

    // ── Turnover / flow ─────────────────────────────────────────────
    case 'afl.turnover': {
      const to = label(p['toSeatId']);
      const reason =
        p['reason'] === 'tackled'
          ? ' (tackled)'
          : p['reason'] === 'no_attack'
            ? ' (no attacking option)'
            : p['viaStoppage']
              ? ' (from the stoppage)'
              : '';
      return `Turnover to ${to}${reason}.`;
    }
    case 'afl.weather.played': {
      const name = typeof p['name'] === 'string' ? (p['name'] as string) : 'Weather';
      const skillMod = typeof p['skillMod'] === 'number' ? (p['skillMod'] as number) : 0;
      const eff = skillMod !== 0 ? ` — Skill ${skillMod} on every shot` : ' — clear, no penalty';
      return `${poss(p['seatId'])} ${name} rolls in${eff}.`;
    }
    case 'afl.time.played': {
      const name = typeof p['name'] === 'string' ? (p['name'] as string) : 'Time';
      const skillMod = typeof p['skillMod'] === 'number' ? (p['skillMod'] as number) : 0;
      const eff = skillMod !== 0 ? ` — Skill ${skillMod} on every shot` : ' — full light, no penalty';
      return `${poss(p['seatId'])} brings on ${name}${eff}.`;
    }
    case 'afl.condition.played': {
      const name = typeof p['name'] === 'string' ? (p['name'] as string) : 'Conditions';
      const spec = (p['spec'] ?? {}) as { type?: string; stat?: string; value?: number; mode?: string };
      const statName = (spec.stat ?? '').replace(/^./, (c) => c.toUpperCase());
      const eff =
        spec.type === 'stat_set'
          ? ` — sets ${statName} = ${spec.value} for all players`
          : spec.mode === 'tier'
            ? ' — contests ignore tier (in/out of position only)'
            : ' — positions do not apply (contests on stat alone)';
      const replaced = typeof p['replaced'] === 'string' && p['replaced'] ? ` (replaces ${p['replaced']})` : '';
      return `${poss(p['seatId'])} set the conditions: ${name}${eff}${replaced}.`;
    }
    case 'afl.quarter.advanced':
      return `Quarter ${p['quarter'] ?? ''} begins${p['windDirection'] != null ? ' — ends swapped, the wind has turned.' : ''}.`;
    case 'afl.match.completed': {
      // A tied match completes with winnerSeatId: null / reason: 'draw'.
      if (!p['winnerSeatId'] || p['reason'] === 'draw') return 'Match over — a draw.';
      const w = label(p['winnerSeatId']);
      return `Match over — ${w} win${w === 'You' ? '' : 's'}.`;
    }

    default:
      return null;
  }
}
